/**
 * Lightweight particle system for visual effects.
 * Budget-aware to stay within frame time limits.
 */

export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;      // 0-1, decreasing
  maxLife: number;    // seconds
  color: string;
  size: number;
  opacity: number;
}

export class ParticleSystem {
  private particles: Particle[] = [];
  private maxParticles: number;

  constructor(mobile = false) {
    this.maxParticles = mobile ? 500 : 2000;
  }

  /** Emit particles from a point in a radial burst */
  emitBurst(
    x: number,
    y: number,
    count: number,
    color: string,
    speed: number,
    lifetime: number,
  ): void {
    if (this.atCapacity) return;

    const available = this.maxParticles - this.particles.length;
    const emitCount = Math.min(count, available);

    for (let i = 0; i < emitCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const spd = Math.random() * speed;
      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * spd,
        vy: Math.sin(angle) * spd,
        life: 1,
        maxLife: lifetime * (0.7 + Math.random() * 0.6), // vary lifetime
        color,
        size: 1 + Math.random() * 2, // 1-3px
        opacity: 0.6 + Math.random() * 0.4,
      });
    }
  }

  /** Emit particles along a line (pathway flow) */
  emitAlongLine(
    sx: number,
    sy: number,
    tx: number,
    ty: number,
    count: number,
    color: string,
    speed: number,
    lifetime: number,
  ): void {
    if (this.atCapacity) return;

    const available = this.maxParticles - this.particles.length;
    const emitCount = Math.min(count, available);

    const dx = tx - sx;
    const dy = ty - sy;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len === 0) return;

    // Perpendicular unit vector
    const nx = -dy / len;
    const ny = dx / len;

    for (let i = 0; i < emitCount; i++) {
      const t = Math.random(); // position along line
      const perpSpeed = (Math.random() - 0.5) * speed * 0.5;
      const alongSpeed = Math.random() * speed * 0.3;

      this.particles.push({
        x: sx + dx * t,
        y: sy + dy * t,
        vx: nx * perpSpeed + (dx / len) * alongSpeed,
        vy: ny * perpSpeed + (dy / len) * alongSpeed,
        life: 1,
        maxLife: lifetime * (0.7 + Math.random() * 0.6),
        color,
        size: 1 + Math.random() * 2,
        opacity: 0.5 + Math.random() * 0.5,
      });
    }
  }

  /** Update all particles (call once per frame) */
  update(deltaSeconds: number): void {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      if (!p) continue;
      p.x += p.vx * deltaSeconds;
      p.y += p.vy * deltaSeconds;
      p.life -= deltaSeconds / p.maxLife;

      if (p.life <= 0) {
        // Swap-remove for performance
        const last = this.particles[this.particles.length - 1];
        if (last) this.particles[i] = last;
        this.particles.pop();
      }
    }
  }

  /** Render all particles to the canvas */
  render(ctx: CanvasRenderingContext2D): void {
    if (this.particles.length === 0) return;

    ctx.save();
    ctx.globalCompositeOperation = 'lighter';

    for (const p of this.particles) {
      const alpha = p.life * p.opacity;
      if (alpha <= 0.01) continue;

      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size * p.life, 0, Math.PI * 2);
      ctx.fillStyle = this.colorWithAlpha(p.color, alpha);
      ctx.fill();
    }

    ctx.restore();
  }

  /** Current particle count */
  get count(): number {
    return this.particles.length;
  }

  /** Clear all particles */
  clear(): void {
    this.particles.length = 0;
  }

  /** Check if we're at budget limit */
  get atCapacity(): boolean {
    return this.particles.length >= this.maxParticles;
  }

  /** Helper: apply alpha to a CSS color string (hex or rgb) */
  private colorWithAlpha(color: string, alpha: number): string {
    if (color.startsWith('rgb(')) {
      const match = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
      if (match) {
        return `rgba(${match[1]}, ${match[2]}, ${match[3]}, ${alpha})`;
      }
    }
    if (color.startsWith('#') && color.length >= 7) {
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    return color;
  }
}
